import toast from 'react-hot-toast';

import {useState} from 'react';

import {VoyagerPlanner, VoyagerPlannerAlarm} from '@/types/api';

import {useQueryAlarmPlanner} from '@/api/queries/voyagers';
import {VoyagerFormSearch} from '@/types/component';
import FormSearchVoyager from './form-search-voyager';
import DatatableWithTheme from '@/components/ui/datatableWIthTheme';
import ShipLoading from '@/components/ui/loader/ShipLoading';
import { FileBarChart2, Ship, Gauge, AlertTriangle, Compass, MapPin, Radio } from 'lucide-react';
import CoordinateUtils from '@/utils/coordinate';

type Props = {};

export default function DetailReportVoyagerPlanner({}: Props) {
    const [voyagerPlannerSelected, setVoyagerPlannerSelected] = useState<VoyagerPlanner | null>(null);

    const {
        error: errorVoyagerAlarm,
        isError: isErrorVoyagerAlarm,
        data: dataVoyagerAlarm,
        isLoading: isLoadingVoyagerAlarm,
        isFetching: isFetchingVoaygerAlarm,
        refetch,
    } = useQueryAlarmPlanner(`${voyagerPlannerSelected?.id}`);

    const columns = [
        {
            name: 'Date / Time',
            cell: (row: VoyagerPlannerAlarm) => (
                <div className="py-2">
                    <div className="text-[11px] font-medium text-gray-900 dark:text-gray-200">
                        {row.created_at ? row.created_at.split(' ')[0] : '-'}
                    </div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400">
                        {row.created_at ? row.created_at.split(' ')[1] : '-'}
                    </div>
                </div>
            ),
            width: '120px',
        },
        {
            name: 'Message',
            cell: (row: VoyagerPlannerAlarm) => (
                <div
                    data-tag="allowRowEvents"
                    className="py-2 text-xs text-gray-700 dark:text-gray-300 whitespace-normal min-w-[200px]"
                >
                    {row.message}
                </div>
            ),
        },
        {
            name: 'Position',
            cell: (row: VoyagerPlannerAlarm) => (
                <div className="py-2 tabular-nums whitespace-nowrap">
                    <div className="text-xs text-gray-900 dark:text-white">Lat: {row.vessel_history.latitude}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Lng: {row.vessel_history.longitude}</div>
                </div>
            ),
            width: '160px',
        },
        {
            name: 'Speed',
            cell: (row: VoyagerPlannerAlarm) => (
                <div className="py-2 text-xs tabular-nums text-gray-900 dark:text-white">
                    {row.vessel_history.speed} kn
                </div>
            ),
            width: '100px',
        },
    ];

    const onSearch = (data: VoyagerFormSearch) => {
        if (!data?.voyager) {
            toast.error('Please select voyager first', {
                position: 'top-center',
            });
            return;
        }

        setVoyagerPlannerSelected(data?.voyager);
        setTimeout(() => {
            refetch();
        }, 200);
    };

    return (
        <div className="space-y-6">
            <div className="relative overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-800 bg-gradient-to-r from-brand-600 to-brand-500 text-white shadow-sm">
                <div
                    className="absolute inset-0 opacity-20 pointer-events-none"
                    style={{
                        backgroundImage:
                            "radial-gradient(circle at 20% 20%, rgba(255,255,255,.45), transparent 40%), radial-gradient(circle at 80% 80%, rgba(255,255,255,.25), transparent 45%)",
                    }}
                />
                <div className="relative p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <div className="h-12 w-12 shrink-0 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center ring-1 ring-white/25">
                            <FileBarChart2 className="h-6 w-6" />
                        </div>
                        <div>
                            <div className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-white/70 font-semibold mb-1">
                                Fleet intelligence
                            </div>
                            <h1 className="text-xl sm:text-2xl font-bold tracking-tight">
                                Voyager Planner Report
                            </h1>
                            <p className="text-sm text-white/80 mt-1 max-w-xl">
                                Vessel route planning, ETA/ETD, and deviation alarms.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <FormSearchVoyager onSearch={onSearch}/>
            {voyagerPlannerSelected && (
                <article className="rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4 sm:p-6 shadow-sm hover:border-brand-500 transition-colors">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                                <span className="text-[11px] text-gray-500 dark:text-gray-400 capitalize">{voyagerPlannerSelected.vessel?.device_type || 'Unknown Type'}</span>
                                <span className="text-[11px] text-gray-500 dark:text-gray-400">·</span>
                                <span className="text-[11px] text-gray-500 dark:text-gray-400 capitalize">{voyagerPlannerSelected.vessel?.ship_type || 'Unknown Class'}</span>
                            </div>
                            <div className="text-base sm:text-lg font-semibold tracking-tight mt-0.5 text-gray-900 dark:text-white">
                                {voyagerPlannerSelected.name}
                            </div>
                            <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                                {voyagerPlannerSelected.vessel?.vessel_name || 'No Vessel Linked'} {voyagerPlannerSelected.vessel_id ? `(ID ${voyagerPlannerSelected.vessel_id})` : ''}
                            </div>
                        </div>
                        <Ship className="h-6 sm:h-8 w-6 sm:w-8 text-gray-400" />
                    </div>

                    <div className="my-5 h-px bg-gray-100 dark:bg-gray-700" />

                    {/* From / To */}
                    <div className="space-y-4 sm:space-y-5 relative pl-1 sm:pl-2">
                        <span className="absolute left-[11px] sm:left-[15px] top-5 bottom-5 w-px border-l-2 border-dashed border-gray-300 dark:border-gray-600" />
                        <div className="flex items-start gap-4 sm:gap-5">
                            <span className="mt-1 h-4 w-4 sm:h-5 sm:w-5 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
                                <span className="h-2 w-2 sm:h-2.5 sm:w-2.5 rounded-full bg-blue-500 dark:bg-blue-400" />
                            </span>
                            <div className="min-w-0">
                                <div className="text-sm font-semibold leading-tight text-gray-900 dark:text-gray-100">Departure</div>
                                <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">{voyagerPlannerSelected.departure}</div>
                            </div>
                        </div>
                        <div className="flex items-start gap-4 sm:gap-5">
                            <span className="mt-0.5 h-4 w-4 sm:h-5 sm:w-5 rounded-full border-[3px] border-brand-500 bg-white dark:bg-gray-800 flex items-center justify-center shrink-0">
                                <span className="h-1 w-1 sm:h-1.5 sm:w-1.5 rounded-full bg-brand-500" />
                            </span>
                            <div className="min-w-0">
                                <div className="text-sm font-semibold leading-tight text-gray-900 dark:text-gray-100">Arrival</div>
                                <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">{voyagerPlannerSelected.arrival}</div>
                            </div>
                        </div>
                    </div>

                    <div className="my-5 h-px bg-gray-100 dark:bg-gray-700" />

                    {/* Telemetry */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        <div className="rounded-xl bg-gray-50 dark:bg-gray-900/50 p-3 sm:p-4 min-w-0">
                            <div className="flex items-center gap-1.5 text-[10px] sm:text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                <Gauge className="h-3.5 w-3.5 sm:h-4 sm:w-4" /> Avg Speed
                            </div>
                            <div className="text-sm sm:text-base font-semibold mt-1 truncate tabular-nums text-gray-900 dark:text-gray-200">
                                {voyagerPlannerSelected.speed} kn
                            </div>
                        </div>
                        <div className="rounded-xl bg-gray-50 dark:bg-gray-900/50 p-3 sm:p-4 min-w-0">
                            <div className="flex items-center gap-1.5 text-[10px] sm:text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                <AlertTriangle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-red-500" /> Over Speed
                            </div>
                            <div className="text-sm sm:text-base font-semibold mt-1 truncate tabular-nums text-gray-900 dark:text-gray-200">
                                {voyagerPlannerSelected.over_speed} kn
                            </div>
                        </div>
                        <div className="rounded-xl bg-gray-50 dark:bg-gray-900/50 p-3 sm:p-4 min-w-0 col-span-2 sm:col-span-1">
                            <div className="flex items-center gap-1.5 text-[10px] sm:text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                <Compass className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-orange-500" /> Off Track
                            </div>
                            <div className="text-sm sm:text-base font-semibold mt-1 truncate tabular-nums text-gray-900 dark:text-gray-200">
                                {voyagerPlannerSelected.off_track} km
                            </div>
                        </div>
                    </div>

                    <div className="my-5 h-px bg-gray-100 dark:bg-gray-700" />
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="rounded-xl bg-gray-50 dark:bg-gray-900/50 p-3 sm:p-4 min-w-0">
                            <div className="flex items-center gap-1.5 text-[10px] sm:text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                <MapPin className="h-3.5 w-3.5 sm:h-4 sm:w-4" /> Route Name
                            </div>
                            <div className="text-sm sm:text-base font-semibold mt-1 text-gray-900 dark:text-gray-200" title={voyagerPlannerSelected.routes?.route_name}>
                                {voyagerPlannerSelected.routes?.route_name || '-'}
                            </div>
                        </div>
                        <div className="rounded-xl bg-gray-50 dark:bg-gray-900/50 p-3 sm:p-4 min-w-0">
                            <div className="flex items-center gap-1.5 text-[10px] sm:text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                <Radio className="h-3.5 w-3.5 sm:h-4 sm:w-4" /> Distance
                            </div>
                            <div className="text-sm sm:text-base font-semibold mt-1 truncate tabular-nums text-gray-900 dark:text-gray-200">
                                {voyagerPlannerSelected.routes?.distance || '0'} NM
                            </div>
                        </div>
                    </div>

                    {voyagerPlannerSelected.notes && (
                        <div className="mt-3 rounded-xl bg-blue-50/50 dark:bg-blue-900/10 p-3 sm:p-4 border border-blue-100/50 dark:border-blue-800/30">
                            <div className="text-[10px] sm:text-xs uppercase tracking-wider text-blue-600 dark:text-blue-400 font-medium mb-1">Notes</div>
                            <p className="text-sm text-gray-700 dark:text-gray-300">{voyagerPlannerSelected.notes}</p>
                        </div>
                    )}
                </article>
            )}

            <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm rounded-2xl p-4 sm:p-6 overflow-x-auto">
                <DatatableWithTheme
                    columns={columns}
                    progressPending={isLoadingVoyagerAlarm || isFetchingVoaygerAlarm}
                    progressComponent={<ShipLoading label="Memuat Data Alarms..." />}
                    data={dataVoyagerAlarm || []}
                    paginationPerPage={10}
                    pagination
                    dense
                />
            </div>
        </div>
    );
}
